/**
 * Web hooks for liability waiver system
 * Using React hooks (useState/useEffect) - can be migrated to TanStack Query later
 */

import { useEffect, useState, useCallback } from 'react';

import {
  listCompanyWaiverTemplates,
  getWaiverTemplate,
  getActiveTemplateForCompany,
  getSignedWaiversForProfile,
  ensureWaiverOnFile,
  uploadWaiverTemplate,
  updateTemplate,
  deleteTemplate,
  saveTemplateFields,
  signWaiver,
  getSignedWaiverUrl,
  getWaiverComplianceStats,
  getAllCompanySignedWaivers,
  type WaiverComplianceStats,
  type CompanySignedWaiver,
} from '../services/damageWaivers';
import type {
  DamageWaiverTemplate,
  DamageWaiverTemplateFieldInsert,
  DamageWaiverTemplateWithFields,
  WaiverRequirementCheck,
  SignWaiverInput,
  DamageWaiverSignature,
} from '../types/damageWaiver.types';

export type { WaiverComplianceStats, CompanySignedWaiver };

/**
 * Hook: List all waiver templates for a company
 */
export function useCompanyWaiverTemplates(companyId: string | null | undefined) {
  const [data, setData] = useState<DamageWaiverTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!companyId) {
      setData([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    listCompanyWaiverTemplates(companyId)
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err : new Error(String(err))))
      .finally(() => setIsLoading(false));
  }, [companyId]);

  return { data, isLoading, error };
}

/**
 * Hook: Get a single waiver template with fields
 */
export function useWaiverTemplate(templateId: string | null | undefined) {
  const [data, setData] = useState<DamageWaiverTemplateWithFields | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!templateId) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    getWaiverTemplate(templateId)
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err : new Error(String(err))))
      .finally(() => setIsLoading(false));
  }, [templateId]);

  return { data, isLoading, error };
}

/**
 * Hook: Get active template for a company
 */
export function useActiveTemplateForCompany(
  companyId: string | null | undefined,
  requiresChild?: boolean
) {
  const [data, setData] = useState<DamageWaiverTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!companyId) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    getActiveTemplateForCompany(companyId, requiresChild)
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err : new Error(String(err))))
      .finally(() => setIsLoading(false));
  }, [companyId, requiresChild]);

  return { data, isLoading, error };
}

/**
 * Hook: Get signed waivers for a user or child profile
 */
export function useSignedWaivers(options: {
  userId?: string | null;
  childProfileId?: string | null;
  companyId?: string | null;
}) {
  const [data, setData] = useState<DamageWaiverSignature[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!options.userId && !options.childProfileId) {
      setData([]);
      return;
    }

    setIsLoading(true);
    setError(null);
    getSignedWaiversForProfile({
      userId: options.userId || undefined,
      childProfileId: options.childProfileId || undefined,
      companyId: options.companyId || undefined,
    })
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err : new Error(String(err))))
      .finally(() => setIsLoading(false));
  }, [options.userId, options.childProfileId, options.companyId]);

  return { data, isLoading, error };
}

/**
 * Hook: Check if waiver is required
 */
export function useWaiverRequirement(options: {
  userId: string | null | undefined;
  companyId: string | null | undefined;
  childProfileId?: string | null | undefined;
}) {
  const [data, setData] = useState<WaiverRequirementCheck | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!options.userId || !options.companyId) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    ensureWaiverOnFile({
      userId: options.userId,
      companyId: options.companyId,
      childProfileId: options.childProfileId || undefined,
    })
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err : new Error(String(err))))
      .finally(() => setIsLoading(false));
  }, [options.userId, options.companyId, options.childProfileId]);

  return { data, isLoading, error };
}

/**
 * Hook: Upload a new waiver template
 */
export function useUploadWaiverTemplate() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const upload = useCallback(async (params: {
    fileData: Blob | ArrayBuffer | Uint8Array;
    companyId: string;
    name: string;
    description?: string;
    contentType?: string;
    requiresChild?: boolean;
    waiverCategory?: 'adult' | 'child' | 'family' | 'general';
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await uploadWaiverTemplate(
        params.fileData,
        params.companyId,
        params.name,
        params.description,
        params.contentType,
        params.requiresChild,
        params.waiverCategory
      );
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { upload, isLoading, error };
}

/**
 * Hook: Update a waiver template
 */
export function useUpdateTemplate() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const update = useCallback(async (params: {
    templateId: string;
    updates: Parameters<typeof updateTemplate>[1];
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await updateTemplate(params.templateId, params.updates);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { update, isLoading, error };
}

/**
 * Hook: Delete a waiver template
 */
export function useDeleteTemplate() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const remove = useCallback(async (params: { templateId: string; companyId: string }) => {
    setIsLoading(true);
    setError(null);
    try {
      await deleteTemplate(params.templateId);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { remove, isLoading, error };
}

/**
 * Hook: Save template fields
 */
export function useSaveTemplateFields() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const save = useCallback(async (params: {
    templateId: string;
    fields: Omit<DamageWaiverTemplateFieldInsert, 'template_id'>[];
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await saveTemplateFields(params.templateId, params.fields);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { save, isLoading, error };
}

/**
 * Hook: Sign a waiver
 */
export function useSignWaiver() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const sign = useCallback(async (input: SignWaiverInput) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await signWaiver(input);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { sign, isLoading, error };
}

/**
 * Hook: Get signed URL for viewing a signed waiver PDF
 */
export function useSignedWaiverUrl(signatureId: string | null | undefined, expiresIn?: number) {
  const [data, setData] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!signatureId) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    getSignedWaiverUrl(signatureId, expiresIn)
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err : new Error(String(err))))
      .finally(() => setIsLoading(false));
  }, [signatureId, expiresIn]);

  return { data, isLoading, error };
}

/**
 * Hook: Waiver compliance stats for a company (signed vs unsigned counts).
 */
export function useWaiverComplianceStats(companyId: string | null | undefined) {
  const [data, setData] = useState<WaiverComplianceStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!companyId) { setData(null); return; }
    setIsLoading(true);
    setError(null);
    getWaiverComplianceStats(companyId)
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err : new Error(String(err))))
      .finally(() => setIsLoading(false));
  }, [companyId]);

  return { data, isLoading, error };
}

/**
 * Hook: All signed waivers for a company — for the owner compliance view.
 */
export function useAllCompanySignedWaivers(companyId: string | null | undefined) {
  const [data, setData] = useState<CompanySignedWaiver[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!companyId) { setData([]); return; }
    setIsLoading(true);
    setError(null);
    getAllCompanySignedWaivers(companyId)
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err : new Error(String(err))))
      .finally(() => setIsLoading(false));
  }, [companyId]);

  return { data, isLoading, error };
}

